import { createContext, FC, ReactNode, useContext } from 'react';
import {
  TripBuilderHotelDataDraftPayload,
  TripPreviewSlotView,
} from 'utils/shared/tripResultBuilderPreview';
import { TripSlotRequirement } from 'ts/shared/interfaces/CreateProposalParams';
import { ProposalList } from 'ts/shared/interfaces/ProposalData';

export type TripBuilderPreviewContextValue = {
  canSave: boolean;
  excludedCardKeys: Record<string, true>;
  requirementByRow: Record<string, TripSlotRequirement>;
  hotelImageBySlot: Record<string, string>;
  externalParam_1?: string;
  toggleExcludedCard: (cardKey: string) => void;
  setRowRequirement: (rowKey: string, value: TripSlotRequirement) => void;
  setHotelImageForSlot: (slotKey: string, url: string | undefined) => void;
  selectBeforeFlight: (slot: TripPreviewSlotView, flightKey: string, isRoundTrip: boolean) => void;
  selectAfterFlight: (slot: TripPreviewSlotView, flightKey: string, isRoundTrip: boolean) => void;
  selectMiddleFlight: (
    slot: TripPreviewSlotView,
    transitionKey: string,
    flightKey: string,
    isRoundTrip: boolean,
  ) => void;
  selectHotel: (slotKey: string, hotelKey: string) => void;
  selectTransfer: (slotKey: string, transferKey: string) => void;
  removeIdbHotel: (cardKey: string) => void;
  removeIdbFlight: (cardKey: string) => void;
  removeIdbTransfer: (cardKey: string) => void;
  getHotelForCard: (cardKey: string) => any;
  getProductForCard: (cardKey: string) => ProposalList | undefined;
  getDraftHotelForCard: (cardKey: string) => any;
  onSaveProductEdit: (updatedProduct: ProposalList) => void;
  onSaveHotelDataDraft: (cardKey: string, payload: TripBuilderHotelDataDraftPayload) => void;
  onSaveDraftHotelData: (cardKey: string, payload: TripBuilderHotelDataDraftPayload) => void;
  getDraftHotelExternalParam: (cardKey: string) => string | undefined;
  onSaveCityName: (slot: {
    slotKey: string;
    city: string;
    checkIn: string;
    checkOut: string;
  }, newCity: string) => void;
};

const TripBuilderPreviewContext = createContext<TripBuilderPreviewContextValue | null>(null);

export const TripBuilderPreviewProvider: FC<{
  value: TripBuilderPreviewContextValue;
  children: ReactNode;
}> = ({ value, children }) => (
  <TripBuilderPreviewContext.Provider value={value}>{children}</TripBuilderPreviewContext.Provider>
);

export const useTripBuilderPreview = () => {
  const context = useContext(TripBuilderPreviewContext);
  if (!context) {
    throw new Error('useTripBuilderPreview must be used within TripBuilderPreviewProvider');
  }
  return context;
};
